import React, {
    forwardRef,
    FunctionComponent,
    MutableRefObject,
    Ref,
    RefCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { VoBScoringProps } from './VobScoring.types';

interface BcScoreIconProps {
    isPositive?: boolean;
    small?: boolean;
}

/**
 * Icon component for displaying VoB scoring.
 *
 * @param {boolean | undefined} isPositive - Determines the icon's state:
 *   - true: Shows a positive (green) thumbs up
 *   - false: Shows a negative (red) thumbs down
 *   - undefined: Shows a neutral (gray) icon
 * @param {boolean | undefined} small - Determines the size of the icon
 */
const BcScoreIcon = forwardRef<SVGSVGElement, BcScoreIconProps>(({ isPositive, small }, ref) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={small ? '15' : '30'}
        height={small ? '15' : '30'}
        fill="none"
        viewBox="0 0 30 30"
        className={classNames('thumb', {
            positive: isPositive !== undefined && isPositive,
            negative: isPositive !== undefined && !isPositive,
        })}
    >
        <rect width="30" height="30" rx="5" className="thumb-bg" />
        <path
            fill="#fff"
            d="M5 11.145h4.071V23H5zM10.688 23V11.085c2.056-2.674 6.418-8.035 7.424-8.083 1.258-.06 1.916 1.078 1.797 2.096-.096.814-.52 4.33-.719 5.987h5.688c1.497 0 1.856 1.557 1.736 2.395s-.838 5.688-1.077 7.365C25.297 22.52 24.399 23 23.38 23z"
        />
    </svg>
));

function mergeRefs<T>(...refs: Ref<T>[]): RefCallback<T> {
    return (value: T) => {
        refs.forEach((originalRef) => {
            const ref = originalRef;

            if (typeof ref === 'function') {
                ref(value);
            } else if (ref && typeof ref === 'object' && 'current' in ref) {
                (ref as MutableRefObject<T | null>).current = value;
            }
        });
    };
}

/**
 * Component that displays a visual indicator for Voice of Brand scoring.
 * Shows a thumbs up/down icon based on whether the response meets the threshold.
 *
 * @param {boolean | undefined} meetsThreshold - Whether the response meets the VOB threshold
 * @param {boolean} isWarningMessage - Whether to display the icon in the warning modal
 * @param {boolean} isInsideEditor - Whether the icon has the properties needed to be used inside the editor
 * @param {boolean} emptyResponse - Whether the response is empty
 */
export const VoBScoring: FunctionComponent<VoBScoringProps> = ({
    meetsThreshold,
    isInsideEditor,
    isWarningMessage,
    emptyResponse,
}) => {
    const { t } = useTranslation('common/response-editor');
    const iconRef = useRef<SVGSVGElement>(null);

    const [pulse, setPulse] = useState(false);
    const prevMeetsThreshold = useRef<boolean | undefined>(undefined);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const isPulsable = prevMeetsThreshold.current !== meetsThreshold;

        if (isPulsable) {
            setPulse(true);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => setPulse(false), 150);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [meetsThreshold]);

    const setTooltipText = () => {
        if (isInsideEditor) {
            setEditorTooltipText();
        } else {
            setNonEditorTooltipText();
        }
    };

    const setEditorTooltipText = () => {
        if (meetsThreshold !== undefined) {
            tooltipText = meetsThreshold ? t('VOB_POSITIVE') : t('VOB_NEGATIVE');
        } else {
            tooltipText = t(`VOB_NEUTRAL${emptyResponse ? '_NO-TEXT' : ''}`);
        }
    };

    const setNonEditorTooltipText = () => {
        if (meetsThreshold !== undefined) {
            tooltipText = meetsThreshold ? t('VOB_POSITIVE_SMALL') : t('VOB_NEGATIVE_SMALL');
        } else {
            tooltipText = t('VOB_NEUTRAL_SMALL');
        }
    };

    let tooltipText;

    setTooltipText();

    return (
        <div className="vob-scoring-container">
            <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip id="vob-scoring-tooltip">{tooltipText}</Tooltip>}
                containerPadding={20}
            >
                {({ ref, ...triggerHandler }) => (
                    <div
                        className={classNames('vob-scoring-container__icon-container', {
                            editor: isInsideEditor,
                            negative: meetsThreshold !== undefined && !meetsThreshold,
                            pulse,
                            warning: isWarningMessage,
                        })}
                        ref={mergeRefs(ref, iconRef)}
                        {...triggerHandler}
                    >
                        <BcScoreIcon isPositive={meetsThreshold} small={!isInsideEditor} />
                    </div>
                )}
            </OverlayTrigger>
        </div>
    );
};

VoBScoring.defaultProps = {
    isInsideEditor: true,
    isWarningMessage: false,
    emptyResponse: false,
};
